import { Button } from "@capacit-com/lara-law-shared-ui";
import { Card, CardContent, CardHeader, CardTitle } from "@capacit-com/lara-law-shared-ui";
import { Spinner } from "@capacit-com/lara-law-shared-ui";
import useWorkspace from "@/hooks/useWorkspace";
import { formatBytes } from "@/lib/utils";
import {
  SelectedWorkspaceVersion,
  WorkspaceDocumentRevision,
  WorkspaceDocumentType,
} from "@/models/workspace/Workspace";
import { useWorkspaceDocumentVersionStore } from "@/stores/workspace-document-version.store";
import { formatDateToDanishLocaleWithTimeOnlyNumbers } from "@/utils/format";
import { FC, useEffect } from "react";
import useAzureStorage from "@/pages/workspace/hooks/useAzureStorage";
import { showErrorToast } from "@/components/custom-toasts/toasts";
import { logger } from "@/lib/logger";
import useUserStore from "@/stores/user.store";
import { canUploadFiles } from "@/pages/workspace/utils/workspaceAccess";
import { toSelectedWorkspaceVersion } from "@/pages/workspace/utils/documentVersionStatus";
import { UploadSource } from "@/components/upload/types/upload-item";

type VersionComponentItemProps = {
  revision: WorkspaceDocumentRevision;
};

const VersionComponentItem: FC<VersionComponentItemProps> = ({ revision }) => {
  const { me } = useUserStore();
  const { downloadFile } = useAzureStorage();
  const {
    workspaceDocument,
    selectedVersion,
    setSelectedVersion,
    setWorkspaceDocument,
    workspace,
  } = useWorkspaceDocumentVersionStore();
  const { changeActiveRevision } = useWorkspace();
  const handleVersionSelect = () => {
    if (workspaceDocument) {
      setSelectedVersion(
        toSelectedWorkspaceVersion(workspaceDocument, revision)
      );
    }
  };

  useEffect(() => {
    if (!selectedVersion) {
      const defaultVersion: SelectedWorkspaceVersion = {
        blobName: workspaceDocument?.revisions[0]?.blobId || "",
        name: workspaceDocument?.name || "",
        updatedAt: workspaceDocument?.revisions[0]?.updatedAt || new Date(),
        revisionVersion: workspaceDocument?.activeRevision || 0,
        documentType:
          workspaceDocument?.documentType || WorkspaceDocumentType.PDF,
        size:
          workspaceDocument?.revisions[workspaceDocument.activeRevision - 1]
            ?.size || 0,
      };
      setSelectedVersion(defaultVersion);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangeActiveRevision = async () => {
    if (workspaceDocument && workspaceDocument.workspaceId) {
      try {
        if (workspaceDocument.documentSource !== UploadSource.LOCAL) {
          const file = await downloadFile(revision.blobId);
          if (!file) {
            showErrorToast(
              "Der opstod en fejl under download af filen. Prøv igen senere."
            );
            return;
          }
        }
        changeActiveRevision.mutate(
          {
            workspaceId: workspaceDocument.workspaceId,
            documentId: workspaceDocument.id,
            requestBody: {
              newActiveRevisionVersion: revision.revisionVersion,
            },
          },
          {
            onSuccess: () => {
              const updatedDocument = {
                ...workspaceDocument,
                activeRevision: revision.revisionVersion,
                blobName: revision.blobId,
                size: revision.size,
              };
              setWorkspaceDocument(updatedDocument);
              setSelectedVersion(
                toSelectedWorkspaceVersion(updatedDocument, revision)
              );
            },
          }
        );
      } catch (error) {
        logger.error("Failed to change active document revision", {
          operation: "changeActiveRevision",
          error,
          workspaceId: workspaceDocument.workspaceId,
          documentId: workspaceDocument.id,
          revisionVersion: revision.revisionVersion,
        });
        showErrorToast(
          "Der opstod en uventet fejl under skift af version. Prøv igen senere."
        );
      }
    }
  };

  const isSelected =
    selectedVersion?.revisionVersion === revision.revisionVersion;
  const isCurrentVersion =
    workspaceDocument?.activeRevision === revision.revisionVersion;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-2">
        <Card
          size="sm"
          className={`w-full text-sm cursor-pointer transition-all rounded-sm hover:shadow-md !py-3 ${
            isSelected ? "border-primary bg-primary/10" : ""
          } `}
          onClick={handleVersionSelect}
        >
          <CardHeader className="!px-3 !py-0 pb-2">
            <CardTitle className="flex items-center justify-between text-base">
              <span>Version {revision.revisionVersion}</span>
              <>
                {isCurrentVersion ? (
                  <span className="text-xs bg-primary text-primary-foreground px-2 py-1 rounded">
                    Aktuel
                  </span>
                ) : (
                  workspace &&
                    me &&
                    canUploadFiles(
                      workspace,
                      me.id,
                      workspace.isParentWorkspace ?? false
                    ) && (
                    <Button
                      variant="ghost"
                      className="text-xs px-2 py-0.5 h-6 rounded-sm hover:bg-muted"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleChangeActiveRevision();
                      }}
                      disabled={changeActiveRevision.isPending}
                    >
                      {changeActiveRevision.isPending ? (
                        <Spinner className="mx-[51px] size-4" />
                      ) : (
                        "Gør til aktuel version"
                      )}
                    </Button>
                  )
                )}
              </>
            </CardTitle>
          </CardHeader>
          <CardContent className="!px-3 !pt-0 !pb-0 space-y-0.5">
            <p className="text-muted-foreground">
              Opdateret:{" "}
              {formatDateToDanishLocaleWithTimeOnlyNumbers(
                new Date(revision.updatedAt)
              )}
            </p>
            <p className="text-muted-foreground">
              Filstørrelse: {formatBytes(revision.size)}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default VersionComponentItem;
